import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import ScheduledAnalysis from "../ScheduledAnalysis/ScheduledAnalysis";
import { isUserAdmin } from "../../utils/authUtils";
import "./Admin.css";

/**
 * Admin Module
 * Admin page with tabbed navigation for admin-only features.
 * @module Admin
 */

const ADMIN_TABS = [
  { key: "schedule-analysis", label: "Schedule Analysis" },
];

const Admin = () => {
  const navigate = useNavigate();
  const isAdmin = isUserAdmin();
  const [activeTab, setActiveTab] = useState(ADMIN_TABS[0].key);

  useEffect(() => {
    if (!isAdmin) {
      navigate("/beagle-chat", { replace: true });
    }
  }, [isAdmin, navigate]);

  if (!isAdmin) {
    return null;
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case "schedule-analysis":
        return <ScheduledAnalysis />;
      default:
        return null;
    }
  };

  return (
    <div
      className="w-full overflow-x-hidden overflow-y-auto h-full"
      style={{
        background: "var(--bg-primary)",
      }}
    >
      {/* Tab Navigation Header */}
      <div className="sticky top-0 z-10 bg-[var(--bg-primary)] border-b border-[var(--border-color)] px-4 sm:px-4 md:px-8 py-3 max-sm:py-2">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 overflow-x-auto flex-1 min-w-0 hide-scrollbar" role="tablist">
            {ADMIN_TABS.map((tab) => {
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  id={`admin-tab-${tab.key}`}
                  onClick={() => setActiveTab(tab.key)}
                  className={[
                    "px-4 py-1.5 rounded-full text-sm font-medium transition-all shrink-0",
                    "border-0 cursor-pointer",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/20 focus-visible:ring-offset-2",
                    "max-sm:text-xs max-sm:px-3",
                    isActive
                      ? "bg-brand-500 text-white shadow-sm font-semibold"
                      : "text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)]",
                  ].join(" ")}
                  aria-selected={isActive}
                  aria-controls={`admin-panel-${tab.key}`}
                  role="tab"
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tab Content — override ScheduledAnalysis page-level styles */}
      <div
        role="tabpanel"
        id={`admin-panel-${activeTab}`}
        aria-labelledby={`admin-tab-${activeTab}`}
        className="px-4 sm:px-4 md:px-8 py-4 max-sm:py-3"
        /*
         * The ScheduledAnalysis component is a standalone page component that
         * sets its own min-height, padding, and background on its root element.
         * These inline styles on the child override those page-level declarations
         * so that the component renders cleanly inside the Admin tab shell.
         *
         * Targets:
         *   .scheduled-analysis           -> min-height, padding, background reset
         *   .scheduled-analysis.menu-closed -> margin-left override
         *   .schedule-header              -> hidden (tab label provides context)
         */
      >
        <div className="admin-content-area">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default Admin;
